'use client';

import {
  type CSSProperties,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';

import ExpandableCard from './ExpandableCard';
import { useEqualCardHeights } from './useEqualCardHeights';
import type {
  ExpandableCardClampLines,
  ExpandableCardItem,
  ExpandableCardResponsiveNumber,
} from './ExpandableCard.types';

type ExpandableCardGridProps = {
  cards: ExpandableCardItem[];
  clampLines?: ExpandableCardClampLines;
  columns?: ExpandableCardClampLines;
  maxCollapsedHeight?: ExpandableCardResponsiveNumber;
  className?: string;
  gridClassName?: string;
  cardClassName?: string;
  viewMoreLabel?: string;
  viewLessLabel?: string;
};

const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

const resolveResponsiveNumber = (
  value: ExpandableCardClampLines,
  width: number,
) => {
  if (typeof value === 'number') return value;

  if (width >= 1280 && value.xl) return value.xl;
  if (width >= 1024 && value.lg) return value.lg;
  if (width >= 768 && value.md) return value.md;
  if (width >= 640 && value.sm) return value.sm;

  return value.base;
};

const resolveOptionalResponsiveNumber = (
  value: ExpandableCardResponsiveNumber,
  width: number,
) => {
  if (typeof value === 'number') return value;

  if (width >= 1280 && value.xl !== undefined) return value.xl;
  if (width >= 1024 && value.lg !== undefined) return value.lg;
  if (width >= 768 && value.md !== undefined) return value.md;
  if (width >= 640 && value.sm !== undefined) return value.sm;

  return value.base;
};

const useViewportWidth = () => {
  const [viewportWidth, setViewportWidth] = useState(1024);

  useIsomorphicLayoutEffect(() => {
    const updateViewportWidth = () => {
      setViewportWidth((current) =>
        current === window.innerWidth ? current : window.innerWidth,
      );
    };

    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);

    return () => {
      window.removeEventListener('resize', updateViewportWidth);
    };
  }, []);

  return viewportWidth;
};

const getCardStyle = (columnCount: number): CSSProperties => {
  if (columnCount <= 1) {
    return {
      flexBasis: '100%',
      maxWidth: '100%',
    };
  }

  const gapCount = columnCount - 1;
  const width = `calc((100% - ${gapCount} * 1.5rem) / ${columnCount})`;

  return {
    flexBasis: width,
    maxWidth: width,
  };
};

const ExpandableCardGrid = ({
  cards,
  clampLines = 4,
  columns = { base: 1, md: 2, xl: 3 },
  maxCollapsedHeight,
  className = '',
  gridClassName = '',
  cardClassName = '',
  viewMoreLabel,
  viewLessLabel,
}: ExpandableCardGridProps) => {
  const viewportWidth = useViewportWidth();
  const resolvedClampLines = resolveResponsiveNumber(clampLines, viewportWidth);
  const resolvedColumnCount = Math.max(
    1,
    Math.round(resolveResponsiveNumber(columns, viewportWidth)),
  );
  const resolvedMaxCollapsedHeight =
    maxCollapsedHeight === undefined
      ? undefined
      : resolveOptionalResponsiveNumber(maxCollapsedHeight, viewportWidth);
  const cardStyle = getCardStyle(resolvedColumnCount);
  const { collapsedHeight: cardHeight, registerCardRef } =
    useEqualCardHeights<HTMLDivElement>(
      cards.length,
      [resolvedClampLines, resolvedColumnCount],
      {
        enabled: maxCollapsedHeight === undefined,
      },
    );
  const collapsedHeight =
    resolvedMaxCollapsedHeight === undefined
      ? cardHeight
      : resolvedMaxCollapsedHeight;

  return (
    <div className={className}>
      <div className={`flex flex-wrap items-start gap-6 ${gridClassName}`}>
        {cards.map((card, index) => (
          <div key={card.id} className="min-w-0 shrink-0" style={cardStyle}>
            <ExpandableCard
              title={card.title}
              description={card.description}
              iconUrl={card.iconUrl}
              iconAlt={card.iconAlt}
              imageUrl={card.imageUrl}
              imageAlt={card.imageAlt}
              alwaysExpandable={card.alwaysExpandable ?? !card.disableExpand}
              disableExpand={card.disableExpand}
              collapsedHeight={collapsedHeight}
              clampLines={resolvedClampLines}
              className={cardClassName}
              measureRef={
                maxCollapsedHeight === undefined
                  ? registerCardRef(index)
                  : undefined
              }
              viewMoreLabel={viewMoreLabel}
              viewLessLabel={viewLessLabel}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExpandableCardGrid;
